import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { SocialPost } from '../../../../common/models/social-scheduler.model';
import { platformConfig } from './PlatformIcon';

interface PostCalendarProps {
    year: number;
    month: number; // 0-indexed
    posts: SocialPost[];
    selectedDay?: string | null;
    onDayClick?: (date: string) => void;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * A day's platform dot. The colour identifies the network, so it stays inline
 * rather than becoming a theme token.
 */
const PlatformDot = ({ platform }: { platform: string }) => (
    <span
        className="size-2 rounded-full"
        style={{ backgroundColor: platformConfig[platform as keyof typeof platformConfig]?.color || '#6B7280' }}
    />
);

export const PostCalendar: React.FC<PostCalendarProps> = ({
    year,
    month,
    posts,
    selectedDay,
    onDayClick,
}) => {
    const { t, i18n } = useTranslation();
    const { days, startOffset } = useMemo(() => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const offset = (firstDay.getDay() + 6) % 7;
        return { days: lastDay.getDate(), startOffset: offset };
    }, [year, month]);

    const postsByDay = useMemo(() => {
        const map: Record<number, SocialPost[]> = {};
        for (const post of posts) {
            const d = new Date(post.scheduledAt);
            if (d.getFullYear() === year && d.getMonth() === month) {
                const day = d.getDate();
                if (!map[day]) map[day] = [];
                map[day].push(post);
            }
        }
        return map;
    }, [posts, year, month]);

    const today = new Date();
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

    const cells: React.ReactNode[] = [];
    for (let i = 0; i < startOffset; i++) {
        cells.push(<div key={`empty-${i}`} />);
    }
    for (let d = 1; d <= days; d++) {
        const dayPosts = postsByDay[d] || [];
        const isToday = isCurrentMonth && today.getDate() === d;
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const isSelected = selectedDay === dateStr;

        // Per-platform counts for tooltip
        const platformCounts: Record<string, number> = {};
        for (const p of dayPosts) {
            platformCounts[p.platform] = (platformCounts[p.platform] || 0) + 1;
        }
        const fullDate = new Date(year, month, d).toLocaleDateString(i18n.language, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
        });

        const tooltipContent = (
            <div className="text-xs">
                <div className={cn('font-medium', dayPosts.length && 'mb-1')}>{fullDate}</div>
                {dayPosts.length === 0 ? (
                    <div className="opacity-80">{t('SOCIAL_SCHEDULER.NO_POSTS_SCHEDULED', 'No posts scheduled')}</div>
                ) : (
                    <div className="flex flex-col gap-0.5">
                        {Object.entries(platformCounts).map(([platform, count]) => (
                            <div key={platform} className="flex items-center gap-1.5">
                                <PlatformDot platform={platform} />
                                <span>
                                    {platformConfig[platform as keyof typeof platformConfig]?.label || platform}: {count}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );

        const ariaLabel = dayPosts.length === 0
            ? t('SOCIAL_SCHEDULER.NO_POSTS_SCHEDULED_ARIA', { date: fullDate, defaultValue: `${fullDate}, no posts scheduled` })
            : t('SOCIAL_SCHEDULER.POSTS_SCHEDULED_ARIA', { date: fullDate, count: dayPosts.length, defaultValue: `${fullDate}, ${dayPosts.length} post${dayPosts.length > 1 ? 's' : ''} scheduled` });

        cells.push(
            <Tooltip key={d}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={() => onDayClick?.(dateStr)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                onDayClick?.(dateStr);
                            }
                        }}
                        aria-pressed={isSelected}
                        aria-label={ariaLabel}
                        className={cn(
                            'min-h-21 rounded-lg border p-2 text-start transition-colors outline-none',
                            'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                            onDayClick ? 'cursor-pointer hover:bg-accent' : 'cursor-default',
                            isSelected ? 'border-foreground bg-accent' : 'border-border bg-card rounded-xl',
                        )}
                    >
                        <div className="mb-1.5 flex items-center justify-between">
                            <span className="text-sm leading-none font-medium text-foreground">{d}</span>
                            {isToday && <span aria-hidden className="size-1.5 rounded-full bg-foreground" />}
                        </div>
                        <div className="flex flex-wrap gap-0.5" aria-hidden>
                            {dayPosts.slice(0, 4).map((p, i) => (
                                <PlatformDot key={i} platform={p.platform} />
                            ))}
                            {dayPosts.length > 4 && (
                                <span className="text-[10px] leading-[8px] font-medium text-muted-foreground">
                                    +{dayPosts.length - 4}
                                </span>
                            )}
                        </div>
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top">{tooltipContent}</TooltipContent>
            </Tooltip>,
        );
    }

    return (
        <div>
            <div className="mb-0.5 grid grid-cols-7 gap-1">
                {WEEKDAYS.map((d) => (
                    <div key={d} className="py-1 text-center text-xs font-medium text-muted-foreground">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">{cells}</div>
        </div>
    );
};
